import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ScheduledReports = () => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    name: '',
    template: '',
    frequency: 'weekly',
    recipients: [],
    enabled: true
  });

  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  const templateOptions = [
    { value: 'monthly-summary', label: 'Monthly Compliance Summary' },
    { value: 'audit-preparation', label: 'Audit Preparation Report' },
    { value: 'team-performance', label: 'Team Performance Analysis' },
    { value: 'executive-dashboard', label: 'Executive Dashboard' }
  ];

  const scheduledReports = [
    {
      id: 'SCH-001',
      name: 'Weekly Compliance Review',
      template: 'Team Performance Analysis',
      frequency: 'Weekly',
      nextRun: '2024-09-23T09:00:00Z',
      recipients: ['sarah.chen@regguard.ai', 'michael.rodriguez@regguard.ai'],
      enabled: true,
      lastRun: '2024-09-16T09:00:00Z',
      status: 'Active'
    },
    {
      id: 'SCH-002',
      name: 'Monthly Executive Summary',
      template: 'Executive Dashboard',
      frequency: 'Monthly',
      nextRun: '2024-10-01T08:00:00Z',
      recipients: ['ceo@regguard.ai', 'cfo@regguard.ai', 'cco@regguard.ai'],
      enabled: true,
      lastRun: '2024-09-01T08:00:00Z',
      status: 'Active'
    },
    {
      id: 'SCH-003',
      name: 'Quarterly Audit Report',
      template: 'Audit Preparation Report',
      frequency: 'Quarterly',
      nextRun: '2024-10-01T10:00:00Z',
      recipients: ['audit@regguard.ai', 'compliance@regguard.ai'],
      enabled: false,
      lastRun: '2024-07-01T10:00:00Z',
      status: 'Paused'
    }
  ];

  const handleCreateSchedule = () => {
    setShowCreateForm(false);
    setNewSchedule({
      name: '',
      template: '',
      frequency: 'weekly',
      recipients: [],
      enabled: true
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'text-success bg-success/10';
      case 'Paused': return 'text-warning bg-warning/10';
      case 'Error': return 'text-error bg-error/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Scheduled Reports</h3>
          <p className="text-sm text-muted-foreground">Automated report generation and delivery</p>
        </div>
        <Button
          variant="default"
          size="sm"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowCreateForm(true)}
        >
          Schedule Report
        </Button>
      </div>
      {/* Create Schedule Form */}
      {showCreateForm && (
        <div className="mb-6 p-4 border border-border rounded-lg bg-muted/20">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-foreground">Create Scheduled Report</h4>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={() => setShowCreateForm(false)}
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input
              label="Report Name"
              placeholder="Enter report name"
              value={newSchedule?.name}
              onChange={(e) => setNewSchedule(prev => ({ ...prev, name: e?.target?.value }))}
            />
            <Select
              label="Report Template"
              options={templateOptions}
              value={newSchedule?.template}
              onChange={(value) => setNewSchedule(prev => ({ ...prev, template: value }))}
            />
            <Select
              label="Frequency"
              options={frequencyOptions}
              value={newSchedule?.frequency}
              onChange={(value) => setNewSchedule(prev => ({ ...prev, frequency: value }))}
            />
            <Input
              label="Recipients"
              placeholder="Enter email addresses (comma separated)"
              description="Separate multiple emails with commas"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <Checkbox
              label="Enable schedule immediately"
              checked={newSchedule?.enabled}
              onChange={(e) => setNewSchedule(prev => ({ ...prev, enabled: e?.target?.checked }))}
            />
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCreateForm(false)}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleCreateSchedule}
              >
                Create Schedule
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Scheduled Reports List */}
      <div className="space-y-4">
        {scheduledReports?.map((report) => (
          <div key={report?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h4 className="font-medium text-foreground">{report?.name}</h4>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(report?.status)}`}>
                    {report?.status}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-muted-foreground">
                  <div>
                    <span className="font-medium">Template:</span> {report?.template}
                  </div>
                  <div>
                    <span className="font-medium">Frequency:</span> {report?.frequency}
                  </div>
                  <div>
                    <span className="font-medium">Next Run:</span> {formatDateTime(report?.nextRun)}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Play"
                  className="h-8 w-8 p-0"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Edit"
                  className="h-8 w-8 p-0"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="Trash2"
                  className="h-8 w-8 p-0 text-error hover:text-error"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                <span>Recipients: {report?.recipients?.length}</span>
                <span>Last run: {formatDateTime(report?.lastRun)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="xs"
                  iconName="Users"
                  iconPosition="left"
                >
                  Manage Recipients
                </Button>
                <Button
                  variant={report?.enabled ? "outline" : "default"}
                  size="xs"
                  iconName={report?.enabled ? "Pause" : "Play"}
                  iconPosition="left"
                >
                  {report?.enabled ? "Pause" : "Resume"}
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {scheduledReports?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">No Scheduled Reports</h4>
          <p className="text-muted-foreground mb-4">Create your first scheduled report to automate compliance reporting</p>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowCreateForm(true)}
          >
            Schedule Your First Report
          </Button>
        </div>
      )}
    </div>
  );
};

export default ScheduledReports;